import React, { useEffect, lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}
import { Toaster } from './components/ui/toaster';
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import FeaturesSection from './components/FeaturesSection';
import AppointmentJourney from './components/AppointmentJourney';
import CTASection from './components/CTASection';
import TestimonialsSection from './components/TestimonialsSection';
import BlogSection from './components/BlogSection';
import FAQSection from './components/FAQSection';
import Footer from './components/Footer';

const CardiologyPage = lazy(() => import('./pages/CardiologyPage'));
const OrthopaedicsPage = lazy(() => import('./pages/OrthopaedicsPage'));
const NephrologyPage = lazy(() => import('./pages/NephrologyPage'));
const EndocrinologyPage = lazy(() => import('./pages/EndocrinologyPage'));
const DermatologyPage = lazy(() => import('./pages/DermatologyPage'));
const DentalPage = lazy(() => import('./pages/DentalPage'));
const ENTPage = lazy(() => import('./pages/ENTPage'));
const ObesityPage = lazy(() => import('./pages/ObesityPage'));
const WellnessPage = lazy(() => import('./pages/WellnessPage'));
const SpecialityServicesPage = lazy(() => import('./pages/SpecialityServicesPage'));
const OptometryPage = lazy(() => import('./pages/OptometryPage'));
const PaediatricsPage = lazy(() => import('./pages/PaediatricsPage'));
const MentalHealthPage = lazy(() => import('./pages/MentalHealthPage'));
const UrologyPage = lazy(() => import('./pages/UrologyPage'));
const OncologyPage = lazy(() => import('./pages/OncologyPage'));
const FertilityPage = lazy(() => import('./pages/FertilityPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const WebinarPage = lazy(() => import('./pages/WebinarPage'));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="w-10 h-10 border-4 border-[#1e3a8a] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

const WHATSAPP_NUMBER = '447700000000'; // TODO: replace with real number
const WHATSAPP_MESSAGE = encodeURIComponent("Hello! I'd like to enquire about your medical services.");

function WhatsAppButton() {
  return (
    <a
      href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat with us on WhatsApp"
      className="fixed bottom-6 right-6 z-50 flex items-center gap-3 group"
    >
      {/* Tooltip */}
      <span className="opacity-0 group-hover:opacity-100 translate-x-2 group-hover:translate-x-0 transition-all duration-300 bg-white text-gray-800 text-sm font-semibold px-4 py-2 rounded-full shadow-lg whitespace-nowrap pointer-events-none">
        Chat with us
      </span>

      {/* Button */}
      <div className="relative w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-110 flex-shrink-0">
        {/* Pulse ring */}
        <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-25" />
        {/* WhatsApp icon */}
        <svg className="w-7 h-7 text-white relative z-10" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
        </svg>
      </div>
    </a>
  );
}

function HomePage() {
  return (
    <div className="App min-h-screen">
      <Header />
      <main>
        <HeroSection />
        <FeaturesSection />
        <AppointmentJourney />
        <CTASection />
        <TestimonialsSection />
        <BlogSection />
        <FAQSection />
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/cardiology-services" element={<CardiologyPage />} />
          <Route path="/orthopaedic-services" element={<OrthopaedicsPage />} />
          <Route path="/nephrology-services" element={<NephrologyPage />} />
          <Route path="/virtual-endocrinology" element={<EndocrinologyPage />} />
          <Route path="/dermatology-service" element={<DermatologyPage />} />
          <Route path="/dental-service" element={<DentalPage />} />
          <Route path="/ent-service" element={<ENTPage />} />
          <Route path="/obesity-service" element={<ObesityPage />} />
          <Route path="/wellness-care-services" element={<WellnessPage />} />
          <Route path="/optometry-service" element={<OptometryPage />} />
          <Route path="/pediatric-service" element={<PaediatricsPage />} />
          <Route path="/mental-health-wellness" element={<MentalHealthPage />} />
          <Route path="/urology-problems" element={<UrologyPage />} />
          <Route path="/oncology-care-service" element={<OncologyPage />} />
          <Route path="/fertility-specialist-consultation" element={<FertilityPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/speciality-services" element={<SpecialityServicesPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/webinars" element={<WebinarPage />} />
        </Routes>
      </Suspense>
      <WhatsAppButton />
      <Toaster />
    </BrowserRouter>
  );
}

export default App;